import { apiFetch, apiUrl } from "@/lib/api";

const BASE = "/api/v1/co_writer";

export interface TalkNoteDocumentSummary {
  id: string;
  title: string;
  /** KB names this note is bound to (same shape as Book.knowledge_bases). */
  knowledge_bases: string[];
  created_at: number;
  updated_at: number;
  preview: string;
}

export interface TalkNoteDocument {
  id: string;
  title: string;
  content: string;
  knowledge_bases: string[];
  created_at: number;
  updated_at: number;
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `Request failed (${res.status}): ${text || res.statusText}`,
    );
  }
  return res.json() as Promise<T>;
}

export async function listTalkNoteDocuments(): Promise<
  TalkNoteDocumentSummary[]
> {
  const res = await apiFetch(apiUrl(`${BASE}/documents`), {
    cache: "no-store",
  });
  const data = await jsonOrThrow<{ documents: TalkNoteDocumentSummary[] }>(res);
  return Array.isArray(data?.documents) ? data.documents : [];
}

export async function createTalkNoteDocument(payload?: {
  title?: string;
  content?: string;
  knowledge_bases?: string[];
}): Promise<TalkNoteDocument> {
  const res = await apiFetch(apiUrl(`${BASE}/documents`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      title: payload?.title ?? null,
      content: payload?.content ?? "",
      knowledge_bases: payload?.knowledge_bases ?? null,
    }),
  });
  return jsonOrThrow<TalkNoteDocument>(res);
}

export async function getTalkNoteDocument(
  docId: string,
): Promise<TalkNoteDocument> {
  const res = await apiFetch(
    apiUrl(`${BASE}/documents/${encodeURIComponent(docId)}`),
    {
      cache: "no-store",
    },
  );
  return jsonOrThrow<TalkNoteDocument>(res);
}

export async function updateTalkNoteDocument(
  docId: string,
  payload: {
    title?: string | null;
    content?: string | null;
    /** Omit/null keeps the stored binding; [] clears it. */
    knowledge_bases?: string[] | null;
  },
): Promise<TalkNoteDocument> {
  const res = await apiFetch(
    apiUrl(`${BASE}/documents/${encodeURIComponent(docId)}`),
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: payload.title ?? null,
        content: payload.content ?? null,
        knowledge_bases: payload.knowledge_bases ?? null,
      }),
    },
  );
  return jsonOrThrow<TalkNoteDocument>(res);
}

export async function deleteTalkNoteDocument(docId: string): Promise<boolean> {
  const res = await apiFetch(
    apiUrl(`${BASE}/documents/${encodeURIComponent(docId)}`),
    {
      method: "DELETE",
    },
  );
  const data = await jsonOrThrow<{ deleted: boolean }>(res);
  return Boolean(data?.deleted);
}
